import {
  TOKEN_COST_CURRENCY,
  USAGE_LIMIT_COST_FRACTION_DIGITS,
} from "../constants/token-usage-cost";
import type { IFormatUsageLimitAmountProps } from "../types/token-budget";
import { ETokenBudgetUnit } from "../types/token-budget";
import { formatDeviceLocaleNumber } from "./number";
import { formatTokenCount, formatUsageTokens } from "./token-count";

// NOTE: NSAI-5612 — a budget in dollars, rendered at cents. Deliberately NOT
// the cost screen's formatCurrency: that one (NSAI-7005) prints a billed
// sub-cent spend as "<$0.01" and an unpriced one as a dash, both statements
// about a spend report. A budget is a round figure an owner typed, and its
// progress line ("$0.00 of $5.00") wants the plain number beside the bar.
const formatBudgetCurrency = (amount: number): string =>
  formatDeviceLocaleNumber({
    value: amount,
    options: {
      style: "currency",
      currency: TOKEN_COST_CURRENCY,
      // NOTE: narrowSymbol renders "$" instead of the locale-dependent "US$".
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: USAGE_LIMIT_COST_FRACTION_DIGITS,
      maximumFractionDigits: USAGE_LIMIT_COST_FRACTION_DIGITS,
    },
  });

// NOTE: NSAI-5612 — renders a budget figure in the denomination its cap is set
// in, so one component can show "8,000 / 10,000" and "$4.20 / $5.00" without
// knowing which it is looking at. Both clients share it, so the same budget
// reads the same on web and on mobile.
//
// Tokens keep the EXACT count the usage-limits overview has always shown: the
// figure sits next to a number the owner typed into the field above it, and a
// cap of 1,500 displayed as "1.5K" no longer matches what they set.
export const formatUsageLimitAmount = ({
  amount,
  unit,
}: IFormatUsageLimitAmountProps): string =>
  unit === ETokenBudgetUnit.USD
    ? formatBudgetCurrency(amount)
    : formatTokenCount(amount);

// NOTE: The same choice for a surface that is read at a glance rather than
// compared against a form field — the threshold notification card, where
// "9K / 10K" has always been the point. Split from the exact variant rather
// than parameterised, because the caller's surface decides this once and a
// boolean at every call site would invite getting it wrong per call.
export const formatCompactUsageLimitAmount = ({
  amount,
  unit,
}: IFormatUsageLimitAmountProps): string =>
  unit === ETokenBudgetUnit.USD
    ? formatBudgetCurrency(amount)
    : formatUsageTokens(amount);
